import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { saveComplexQuestionResult, clearQuestionResult, UserExampleAudio } from "../redux/Action";
import { RootState } from "../redux/Store";
import { useReactMediaRecorder } from "react-media-recorder";
import RollbackButton from "../components/ui/RollbackButton";

const Question15: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get child info and question results from Redux store
  const childInfo = useSelector((state: RootState) => (state as any).childInfo);
  const questions = useSelector((state: RootState) => (state as any).questions);
  const childName = childInfo?.childName || "your child";

  // Find existing result for this question using new structure
  const existingResult = questions?.Q15;
  const childGender = childInfo?.gender || "unknown";
  
  // Get gender-specific pronouns
  const getPronoun = (type: "subject" | "object" | "possessive") => {
    if (childGender === "male") {
      return type === "subject" ? "he" : type === "object" ? "him" : "his";
    } else if (childGender === "female") {
      return type === "subject" ? "she" : type === "object" ? "her" : "her";
    } else {
      return type === "subject" ? "he/she" : type === "object" ? "him/her" : "his/her";
    }
  };

  // State for main answer and sub-questions
  const [mainAnswer, setMainAnswer] = useState<"yes" | "no" | null>(null);
  const [copyingAnswers, setCopyingAnswers] = useState<("yes" | "no" | null)[]>([]);
  const [score, setScore] = useState<0 | 1 | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // State for one-by-one display
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState<"main" | "example" | "copying">("main");
  const [userExample, setUserExample] = useState<string>("");
  const [examplesSaved, setExamplesSaved] = useState<boolean>(false);
  const [noExamplesChecked, setNoExamplesChecked] = useState<boolean>(false);
  const [userExampleAudio, setUserExampleAudio] = useState<UserExampleAudio | null>(null);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [recordStartTs, setRecordStartTs] = useState<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState<number>(0);

  const { status, startRecording, stopRecording, mediaBlobUrl } = useReactMediaRecorder({ audio: true, video: false });
  const isRecording = status === "recording";
  // Track most recently answered for one-step rollback
  const [lastAnsweredQuestion, setLastAnsweredQuestion] = useState<{
    section: "main" | "copying";
    index?: number;
  } | null>(null);

  useEffect(() => {
    let timer: any;
    if (isRecording && recordStartTs) {
      timer = setInterval(() => setElapsedMs(Date.now() - recordStartTs), 200);
    }
    return () => timer && clearInterval(timer);
  }, [isRecording, recordStartTs]);

  useEffect(() => {
    if (!isRecording) {
      setRecordStartTs(null);
    }
  }, [isRecording]);

  const blobToDataUrl = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  useEffect(() => {
    const process = async () => {
      try {
        if (!mediaBlobUrl) return;
        const resp = await fetch(mediaBlobUrl);
        const blob = await resp.blob();
        const dataUrl = await blobToDataUrl(blob);
        const audio: UserExampleAudio = {
          dataUrl,
          mimeType: blob.type || "audio/webm",
          durationMs: elapsedMs,
          createdAt: new Date().toISOString(),
        };
        setUserExampleAudio(audio);
        setNoExamplesChecked(false);
      } catch (e) {
        setRecordError("Failed to prepare audio preview.");
      }
    };
    process();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaBlobUrl]);

  const handleMicClick = async () => {
    if (score !== null || examplesSaved || noExamplesChecked) return;
    setRecordError(null);
    try {
      if (!isRecording) {
        setRecordStartTs(Date.now());
        await startRecording();
      } else {
        await stopRecording();
      }
    } catch (e) {
      setRecordError("Microphone not available or permission denied.");
    }
  };

  const copyingScenarios = [
    "Stick out your tongue?",
    "Make a funny sound?",
    "Wave good bye?",
    "Clap your hands?",
    "Put your fingers to your lips to signal 'Shhh'?",
    "Blow a kiss?"
  ];

  // Effect to restore state from existing result using new structure
  useEffect(() => {
    if (isResetting) return;
    if (existingResult?.completed) {
      setIsRestoring(true);
      
      // Restore main answer
      setMainAnswer(existingResult.answer);
      
      // Restore copying answers from children
      if (existingResult.children && Array.isArray(existingResult.children)) {
        const answers = existingResult.children.map((child: any) => child.answer);
        setCopyingAnswers(answers);
      }
      
      // Restore the result score
      const finalScore = existingResult.result === "pass" ? 0 : 1;
      setScore(finalScore);
      
      // Restore user example if it exists
      if (existingResult.userExample) {
        if (existingResult.userExample === "No examples provided") {
          setNoExamplesChecked(true);
          setExamplesSaved(true);
        } else {
          setUserExample(existingResult.userExample);
          setExamplesSaved(true);
        }
      }
      if (existingResult.userExampleAudio) {
        setUserExampleAudio(existingResult.userExampleAudio);
        setExamplesSaved(true);
      }
      
      // Restore checkbox and saved states
      if (existingResult.noExamplesChecked !== undefined) {
        setNoExamplesChecked(existingResult.noExamplesChecked);
      }
      if (existingResult.examplesSaved !== undefined) {
        setExamplesSaved(existingResult.examplesSaved);
      }
      
      // Set current section based on what's been answered
      if (copyingAnswers.some(answer => answer !== null)) {
        setCurrentSection("copying");
        // Set current question index to the next unanswered question
        const nextUnansweredIndex = copyingAnswers.findIndex(answer => answer === null);
        setCurrentQuestionIndex(nextUnansweredIndex >= 0 ? nextUnansweredIndex : copyingScenarios.length);
      }
      
      setTimeout(() => setIsRestoring(false), 100);
    } else if (existingResult === null || existingResult === undefined) {
      setMainAnswer(null);
      setCopyingAnswers([]);
      setScore(null);
      setCurrentQuestionIndex(0);
      setCurrentSection("main");
      setUserExample("");
      setExamplesSaved(false);
      setNoExamplesChecked(false);
      setUserExampleAudio(null);
    }
  }, [existingResult, isResetting, copyingScenarios.length]);

  // Auto-proceed from example to copying when requirement is met
  useEffect(() => {
    if (mainAnswer === "yes" && currentSection === "example" && (examplesSaved || noExamplesChecked)) {
      setCurrentSection("copying");
    }
  }, [mainAnswer, currentSection, examplesSaved, noExamplesChecked]);

  // Calculate score based on flowchart logic
  useEffect(() => {
    if (isRestoring) return;
    if (mainAnswer !== null) {
      // Check if we have all necessary answers for copying scenarios
      const copyingComplete = copyingAnswers.length === copyingScenarios.length && 
                             copyingAnswers.every(answer => answer !== null);
      
      if (copyingComplete) {
        // Count "Yes" answers
        const yesCount = copyingAnswers.filter(answer => answer === "yes").length;
        
        if (yesCount >= 2) {
          // "Yes to two or more" → PASS
          setScore(0);
        } else {
          // "Yes to one or none" → FAIL
          setScore(1);
        }
      } else {
        // Reset score if not all copying questions are answered
        setScore(null);
      }
    }
  }, [mainAnswer, copyingAnswers, copyingScenarios.length, isRestoring]);

  // Save result when score is calculated using modern structure
  useEffect(() => {
    if (isRestoring) return;
    if (score !== null) {
      const result = score === 0 ? "pass" : "fail";
      const complexData: Record<string, any> = {
        completed: true,
        // Modern standardized keys only
        "Q15": (mainAnswer || "no"),
        "Q15.1": copyingAnswers[0] ?? undefined,
        "Q15.2": copyingAnswers[1] ?? undefined,
        "Q15.3": copyingAnswers[2] ?? undefined,
        "Q15.4": copyingAnswers[3] ?? undefined,
        "Q15.5": copyingAnswers[4] ?? undefined,
        "Q15.6": copyingAnswers[5] ?? undefined,
        userExample: noExamplesChecked ? "No examples provided" : (userExample || undefined),
        userExampleAudio: userExampleAudio || undefined,
        noExamplesChecked,
        examplesSaved,
      };
      
      dispatch(
        saveComplexQuestionResult(
          15,
          result,
          complexData
        )
      );
    }
  }, [score, mainAnswer, copyingAnswers, userExample, noExamplesChecked, examplesSaved, userExampleAudio, dispatch, isRestoring]);

  const handleMainAnswer = (answer: "yes" | "no") => {
    setMainAnswer(answer);
    setScore(null);
    setCurrentQuestionIndex(0);
    setLastAnsweredQuestion({ section: "main" });
    
    if (answer === "yes") {
      // For "yes" answers, show example box first
      setCurrentSection("example");
      setUserExample("");
      setExamplesSaved(false);
      setNoExamplesChecked(false);
      setCopyingAnswers(new Array(copyingScenarios.length).fill(null));
    } else {
      // For "no" answers, go directly to copying scenarios
      setCurrentSection("copying");
      setCopyingAnswers(new Array(copyingScenarios.length).fill(null));
    }
  };

  const handleCopyingAnswer = (answer: "yes" | "no") => {
    const newCopyingAnswers = [...copyingAnswers];
    newCopyingAnswers[currentQuestionIndex] = answer;
    setCopyingAnswers(newCopyingAnswers);
    setLastAnsweredQuestion({ section: "copying", index: currentQuestionIndex });
    
    // Auto-advance to next question
    if (currentQuestionIndex < copyingScenarios.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
    // If not all answered, stay on the current question
  };

  const handleResetQuestion = () => {
    setShowResetModal(true);
  };

  const handleConfirmReset = () => {
    setIsResetting(true);
    dispatch(clearQuestionResult(15));
    setMainAnswer(null);
    setCopyingAnswers([]);
    setScore(null);
    setCurrentQuestionIndex(0);
    setCurrentSection("main");
    setUserExample("");
    setExamplesSaved(false);
    setNoExamplesChecked(false);
    setShowResetModal(false);
    setTimeout(() => setIsResetting(false), 100);
  };

  const handleCancelReset = () => {
    setShowResetModal(false);
  };

  const handleNext = () => {
    navigate("/question/16");
  };

  const handlePrev = () => {
    navigate("/question/14");
  };

  const getCurrentQuestion = () => {
    if (currentSection === "copying") {
      return copyingScenarios[currentQuestionIndex];
    }
    return "";
  };

  const getCurrentAnswer = () => {
    if (currentSection === "copying") {
      return copyingAnswers[currentQuestionIndex];
    }
    return undefined;
  };

  const getAnsweredCount = () => {
    if (currentSection === "copying") {
      return copyingAnswers.filter(answer => answer !== null).length;
    }
    return 0;
  };

  const getTotalQuestions = () => {
    if (currentSection === "copying") {
      return copyingScenarios.length;
    }
    return 0;
  };

  const isExampleLocked = examplesSaved || noExamplesChecked;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-indigo-200">
      <style>
        {`
          @keyframes fadeInBounce {
            0% { opacity: 0; transform: translateY(-20px); }
            50% { opacity: 1; transform: translateY(5px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto bg-white/80 rounded-2xl shadow-2xl p-6 md:p-8">
          
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-600">Question 15 of 20</span>
              <span className="text-sm font-medium text-gray-600">75%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-2 rounded-full" style={{ width: '75%' }}></div>
            </div>
          </div>

          {/* Main Question */}
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                15
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 text-left">
                Does {childName} try to copy what you do?
              </h1>
            </div>
            
            {/* Main Answer Buttons - Horizontal Layout */}
            <div className="flex gap-4 mb-6 justify-center">
              <button
                onClick={() => handleMainAnswer("yes")}
                className={`px-6 py-3 rounded-full font-semibold transition-all border-2 flex items-center justify-center min-w-[120px] ${mainAnswer === "yes"
                    ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-purple-500 shadow-lg"
                    : "bg-white text-gray-700 border-purple-200 hover:border-purple-300 hover:bg-purple-50"
                }`}
              >
                {mainAnswer === "yes" && <span className="mr-2">✓</span>}
                Yes
              </button>
              <button
                onClick={() => handleMainAnswer("no")}
                className={`px-6 py-3 rounded-full font-semibold transition-all border-2 flex items-center justify-center min-w-[120px] ${mainAnswer === "no"
                    ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white border-purple-500 shadow-lg"
                    : "bg-white text-gray-700 border-purple-200 hover:border-purple-300 hover:bg-purple-50"
                }`}
              >
                {mainAnswer === "no" && <span className="mr-2">✓</span>}
                No
              </button>
            </div>
          </div>

          {/* user example input */}
          {mainAnswer === "yes" && (currentSection === "example" || (examplesSaved || noExamplesChecked)) && (
            <div className="mb-6">
              <p className="text-gray-800 font-semibold mb-6 text-center text-lg">
                Please give me an example of when {childName} tries to copy what you do.
              </p>
              
              <div className="space-y-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                {/* Labels positioned above the textarea in separate boxes */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Left side - Description box */}
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2 text-left">
                      Describe {childName}'s behavior
                    </h4>
                    <p className="text-sm text-gray-900 text-left">
                      When {childName} tries to copy you:
                    </p>
                  </div>
                  
                  {/* Right side - Info button box */}
                  <div className="bg-blue-100 border border-blue-200 rounded-lg p-4 flex items-center gap-2">
                    <div className="w-5 h-5 bg-gray-800 text-white rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold">i</span>
                    </div>
                    <span className="text-xs text-gray-900 text-left">
                      This helps us understand {childName}'s specific copying behaviors.
                    </span>
                  </div>
                </div>

                {/* Full width input field with mic/recorder */}
                <div className="space-y-4">
                  {!userExampleAudio ? (
                    <div className="relative">
                      <textarea
                        id="userExample"
                        value={userExample}
                        onChange={(e) => setUserExample(e.target.value)}
                        placeholder="Enter your example here..."
                        className={`w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none ${
                          (score !== null || isExampleLocked) ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        rows={2}
                        disabled={score !== null || isExampleLocked}
                      />
                      {/* Mic / Stop inside textarea */}
                      <button
                        type="button"
                        onClick={handleMicClick}
                        disabled={score !== null || isExampleLocked}
                        className={`absolute right-3 bottom-3 w-7 h-7 rounded-full flex items-center justify-center ${(score !== null || isExampleLocked) ? 'text-gray-300' : isRecording ? 'bg-red-600 text-white animate-pulse' : 'text-gray-600 hover:text-gray-800'}`}
                        title={isRecording ? 'Stop recording' : 'Start recording'}
                      >
                        {isRecording ? (
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="6" width="12" height="12" rx="2" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M7 4a3 3 0 016 0v4a3 3 0 11-6 0V4zm4 10.93A7.001 7.001 0 0017 8a1 1 0 10-2 0A5 5 0 015 8a1 1 0 00-2 0 7.001 7.001 0 006 6.93V17H6a1 1 0 100 2h8a1 1 0 100-2h-3v-2.07z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      {isRecording && (
                        <div className="absolute left-3 bottom-3 text-xs text-red-700 font-medium">
                          Rec {Math.floor(elapsedMs / 1000)}s
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-purple-700 font-medium">Audio example:</p>
                        <button
                          type="button"
                          onClick={() => { setUserExampleAudio(null); setElapsedMs(0); setExamplesSaved(false); setCurrentSection("example"); }}
                          className="inline-flex items-center gap-1 px-2 py-1 border border-purple-300 rounded-md text-xs text-purple-700 hover:bg-purple-100"
                          disabled={score !== null || isExampleLocked}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h18v2H3V6zm2 4h14l-1.5 10.5A2 2 0 0115.52 22H8.48a2 2 0 01-1.98-1.5L5 10z"/></svg>
                          Discard
                        </button>
                      </div>
                      <audio controls src={userExampleAudio.dataUrl} className="w-full mt-2" />
                      {/* <div className="text-xs text-gray-600 mt-1">{Math.round((userExampleAudio.durationMs || 0) / 1000)}s</div> */}
                    </div>
                  )}
                  {recordError && (
                    <div className="text-xs text-red-600">{recordError}</div>
                  )}
                </div>
              </div>
              
              {/* Checkbox and Save button row */}
              <div className="flex items-center justify-between mt-4">
                {/* Checkbox for no examples */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="noExamples"
                    checked={noExamplesChecked}
                    onChange={(e) => {
                      setNoExamplesChecked(e.target.checked);
                      // When checkbox is checked, set saved state to true
                      if (e.target.checked) {
                        setExamplesSaved(true);
                        setUserExampleAudio(null);
                      } else {
                        // When unchecked, clear the saved state but keep the example box visible
                        setExamplesSaved(false);
                        setUserExample("");
                        setUserExampleAudio(null);
                        // Reset current section back to example to hide follow-up questions
                        setCurrentSection("example");
                      }
                    }}
                    disabled={score !== null || isExampleLocked}
                    className={`w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 ${
                      (score !== null || isExampleLocked) ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                  <label htmlFor="noExamples" className="text-sm text-gray-700">
                    I don't have any example for now
                  </label>
                </div>
                
                {/* Save & Next button */}
                <button
                  onClick={() => {
                    // Save the example and set saved state
                    if (userExample.trim() !== "" || userExampleAudio) {
                      setExamplesSaved(true);
                      setCurrentSection("copying");
                    }
                  }}
                  disabled={(userExample.trim() === "" && !userExampleAudio && !noExamplesChecked) || score !== null || isExampleLocked}
                  className={`px-6 py-2 text-sm rounded-md transition-colors shadow-sm font-medium ${
                    (userExample.trim() === "" && !userExampleAudio && !noExamplesChecked) || score !== null || isExampleLocked
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                      : examplesSaved
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  {examplesSaved ? "Saved ✓" : "Save & Next >"}
                </button>
              </div>
            </div>
          )}

          {/* Copying Scenarios Section */}
          {(currentSection === "copying" || copyingAnswers.some(answer => answer !== null)) && (
            <div className="mb-6">
              <div className="text-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">
                  Does {childName} try to copy you if you...
                </h3>
              </div>
              
              {/* Box Container for Copying Questions */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6">
                {/* Show all answered copying questions */}
                {copyingScenarios.map((question, idx) => {
                  if (copyingAnswers[idx] !== null && copyingAnswers[idx] !== undefined) {
                    return (
                      <div key={idx} className="flex items-center justify-between bg-white rounded-lg p-4 border border-purple-200 mb-3">
                        <span className="text-gray-700 font-medium text-md text-left">
                          {idx + 1}. {question}
                        </span>
                        <div className="flex items-center gap-2">
                          <div className={`px-4 py-2 rounded-lg text-md font-semibold ${copyingAnswers[idx] === "yes" ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white" : "bg-gradient-to-r from-purple-500 to-indigo-500 text-white"}`}>
                            {copyingAnswers[idx] === "yes" ? "YES" : "NO"}
                          </div>
                          {lastAnsweredQuestion && lastAnsweredQuestion.section === "copying" && lastAnsweredQuestion.index === idx && (
                            <RollbackButton
                              onClick={() => {
                                const next = [...copyingAnswers];
                                next[idx] = null;
                                setCopyingAnswers(next);
                                setCurrentSection("copying");
                                setCurrentQuestionIndex(idx);
                                setScore(null);
                                setLastAnsweredQuestion(null);
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}

                {/* Current Copying Question - Show only if current question is not answered */}
                {copyingAnswers[currentQuestionIndex] === null && (
                  <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-purple-200 mb-3">
                    <span className="text-gray-700 font-medium text-md text-left">
                      {currentQuestionIndex + 1}. {getCurrentQuestion()}
                    </span>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleCopyingAnswer("yes")}
                        className="px-4 py-2 rounded-lg text-md font-semibold transition-all border-2 bg-white text-gray-700 border-purple-400 hover:border-purple-500 hover:bg-purple-50"
                      >
                        YES
                      </button>
                      <button
                        onClick={() => handleCopyingAnswer("no")}
                        className="px-4 py-2 rounded-lg text-md font-semibold transition-all border-2 bg-white text-gray-700 border-purple-400 hover:border-purple-500 hover:bg-purple-50"
                      >
                        NO
                      </button>
                    </div>
                  </div>
                )}


              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={handlePrev}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition-all"
            >
              Previous
            </button>
            
            {/* Reset Question Button - Only show if question is completed */}
            {existingResult?.completed && (
              <button
                onClick={handleResetQuestion}
                className="px-4 py-3 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-all flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Retry Question
              </button>
            )}
            
            <button
              onClick={handleNext}
              disabled={score === null}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${score !== null
                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Reset Question 15
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to reset this question? This will clear all your answers and you'll need to answer the question again.
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleCancelReset}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmReset}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all"
                >
                  Reset Question
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Question15; 